import { useEffect, useState } from 'react';
import { postUserAuditLogWorkspace } from '@/apis/core/UserAuditLog/post/postAuditLogWorkspace';
import type { WorkspaceRecentActivityEntry } from './Landingpage.types';

type RecentWorkspaceItem = { id: string };

export const useLandingPageWorkspaceActivity = ({
  isCompactTheme,
  recentItemsWithAvatars,
  getAccessToken,
}: {
  isCompactTheme: boolean;
  recentItemsWithAvatars: RecentWorkspaceItem[];
  getAccessToken: () => Promise<string>;
}) => {
  const [workspaceRecentActivity, setWorkspaceRecentActivity] = useState<
    Record<string, WorkspaceRecentActivityEntry>
  >({});

  useEffect(() => {
    if (!isCompactTheme || recentItemsWithAvatars.length === 0) return;

    let isCancelled = false;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    const workspaceIds = recentItemsWithAvatars
      .slice(0, 8)
      .map((item) => item.id);

    const fetchRecentActivity = async () => {
      const requests = workspaceIds.map(async (workspaceId) => {
        try {
          const response = await postUserAuditLogWorkspace({
            getAccessToken,
            workspaceId,
            body: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              results: '10',
              offset: '0',
              users: [],
            },
          });
          const responses = Array.isArray(response) ? response : [response];
          const logs = responses.flatMap((res) => res.logs ?? []);
          if (!logs.length) return [workspaceId, null] as const;

          logs.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
          const latest = logs[0];
          return [
            workspaceId,
            {
              action: latest.action,
              entityType: latest.entityType,
              timestamp: new Date(latest.date).toLocaleString(),
            },
          ] as const;
        } catch (err: unknown) {
          console.error(
            '[useLandingPageWorkspaceActivity] Audit log fetch failed:',
            err instanceof Error ? err : new Error(String(err)),
          );
          return [workspaceId, null] as const;
        }
      });

      const results = await Promise.all(requests);
      if (isCancelled) return;

      setWorkspaceRecentActivity((prev) => {
        const next = { ...prev };
        results.forEach(([workspaceId, latest]) => {
          if (latest) {
            next[workspaceId] = latest;
          }
        });
        return next;
      });
    };

    void fetchRecentActivity();

    return () => {
      isCancelled = true;
    };
  }, [isCompactTheme, recentItemsWithAvatars, getAccessToken]);

  return workspaceRecentActivity;
};
